/**
 * Módulo para manejar la síntesis de voz usando Azure Speech Services
 * Esta versión utiliza un Web Worker para mejor control y rendimiento
 */
import { handleTTSError, Logger } from '@/utils/azureOpenAi/errorHandler';

// Variables de estado
let worker: Worker | null = null;
let initialized = false;
let isSpeaking = false;
let audioQueue: { audioData: Uint8Array, isLastChunk: boolean }[] = [];
let currentAudioSource: AudioBufferSourceNode | null = null;
let audioContext: AudioContext | null = null;
let apiKey = '';
let region = '';
let activeSynthesisCallbacks: {
  onStart?: () => void;
  onEnd?: () => void;
  onError?: (error: any) => void;
  onProgress?: (progress: { processed: number, total: number, percentage: number }) => void;
} | null = null;

/**
 * Inicializa el sistema con las credenciales de Azure
 */
export function initializeSpeechSDK(
    apiKeyParam: string, 
    regionParam: string
): boolean {
    try {
        apiKey = apiKeyParam;
        region = regionParam;
        
        try {
            audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        } catch (e) {
            const audioError = handleTTSError(e, 'initializeSpeechSDK.audioContext');
            Logger.error('AzureTTS', 'Error al crear AudioContext', audioError);
            return false;
        }
        
        if (!worker) {
            try {
                import('./tts-worker?worker')
                    .then(workerModule => {
                        worker = new workerModule.default();
                        setupWorkerListeners();
                        
                        worker.postMessage({
                            type: 'init',
                            payload: {
                                apiKey,
                                region
                            }
                        });
                        
                        Logger.tts('Worker TTS creado e inicializado');
                    })
                    .catch(error => {
                        const workerError = handleTTSError(error, 'initializeSpeechSDK.workerModule');
                        Logger.error('AzureTTS', 'Error al cargar el módulo del worker', workerError);
                        initialized = false;
                    });
            } catch (e) {
                const workerCreateError = handleTTSError(e, 'initializeSpeechSDK.createWorker');
                Logger.error('AzureTTS', 'Error al crear Worker TTS', workerCreateError);
                worker = null;
                return false;
            }
        }
        
        initialized = true;
        return true;
    } catch (error) {
        const initError = handleTTSError(error, 'initializeSpeechSDK');
        Logger.error('AzureTTS', 'Error al inicializar Azure TTS', initError);
        return false;
    }
}

/**
 * Configura los listeners para mensajes del worker
 */
function setupWorkerListeners(): void {
    if (!worker) return;
    
    worker.onmessage = (event) => {
        const { type, payload } = event.data;
        
        switch (type) {
            case 'ready':
                Logger.tts('TTS Worker listo');
                break;
                
            case 'initialized':
                Logger.tts('TTS Worker inicializado con credenciales');
                break;
                
            case 'synthesizing':
                Logger.tts(`Sintetizando: ${payload.text.substring(0, 50) + (payload.text.length > 50 ? "..." : "")}`);
                activeSynthesisCallbacks?.onStart?.();
                break;
                
            case 'audioChunk':
                audioQueue.push({ 
                    audioData: payload.audioData,
                    isLastChunk: payload.isLastChunk
                });
                
                if (!currentAudioSource) {
                    playNextInQueue();
                }
                break;
                
            case 'progress':
                Logger.tts(`Progreso síntesis: ${payload.processed}/${payload.total} (${payload.percentage}%)`);
                activeSynthesisCallbacks?.onProgress?.(payload);
                break;
                
            case 'completed':
                Logger.tts('Síntesis completada');
                break;
                
            case 'error':
                const workerError = handleTTSError(new Error(payload.message), 'workerMessage');
                Logger.error('AzureTTS', 'Error en worker TTS', workerError);
                isSpeaking = false;
                activeSynthesisCallbacks?.onError?.(payload);
                activeSynthesisCallbacks = null;
                break;
                
            case 'cancelled':
                Logger.tts('Síntesis cancelada');
                break;

            case 'notSynthesizing':
                Logger.tts('No hay síntesis activa que detener');
                break;
                
            default:
                Logger.warn('AzureTTS', `Mensaje no reconocido del worker: ${type}`);
        }
    };
    
    worker.onerror = (error) => {
        const workerGeneralError = handleTTSError(error, 'worker.onerror');
        Logger.error('AzureTTS', 'Error en Worker TTS', workerGeneralError);
        isSpeaking = false;
        activeSynthesisCallbacks?.onError?.({ message: "Error en Worker TTS" });
        activeSynthesisCallbacks = null;
    };
}

/**
 * Reproduce el siguiente elemento en la cola de audio
 */
function playNextInQueue(): void {
    if (!audioContext || audioQueue.length === 0 || !isSpeaking) {
        if (audioQueue.length === 0 && isSpeaking) {
            Logger.debug('AzureTTS', 'Cola de audio vacía, esperando más chunks...');
        }
        return;
    }
    
    const { audioData, isLastChunk } = audioQueue.shift()!;
    
    try {
        audioContext.decodeAudioData(
            audioData.buffer,
            (audioBuffer) => {
                if (!isSpeaking) return;
                
                const source = audioContext!.createBufferSource();
                source.buffer = audioBuffer;
                source.connect(audioContext!.destination);
                
                currentAudioSource = source;
                
                source.onended = () => {
                    currentAudioSource = null;
                    
                    if (isLastChunk) {
                        if (audioQueue.length === 0) {
                            Logger.tts('Reproducción de audio completada (último fragmento)');
                            
                            if (isSpeaking) {
                                isSpeaking = false;
                                const callbacks = activeSynthesisCallbacks;
                                activeSynthesisCallbacks = null;
                                callbacks?.onEnd?.();
                            }
                        }
                    }
                    
                    playNextInQueue();
                };
                
                source.start(0);
                Logger.debug('AzureTTS', 'Reproduciendo fragmento de audio');
                
            },
            (error) => {
                const decodeError = handleTTSError(error, 'decodeAudioData');
                Logger.error('AzureTTS', 'Error al decodificar audio', decodeError);
                currentAudioSource = null;
                playNextInQueue();
            }
        );
    } catch (error) {
        const processError = handleTTSError(error, 'playNextInQueue');
        Logger.error('AzureTTS', 'Error al procesar audio', processError);
        currentAudioSource = null;
        playNextInQueue();
    }
}

/**
 * Sintetiza y reproduce texto a voz
 */
export async function synthesizeAndPlayText(
    text: string,
    callbacks: {
        onStart?: () => void;
        onEnd?: () => void;
        onError?: (error: any) => void;
        onProgress?: (progress: any) => void;
    } = {}
): Promise<void> {
    if (!initialized || !worker) {
        Logger.error('AzureTTS', 'Azure TTS no está inicializado');
        callbacks.onError?.({ message: "Sistema TTS no inicializado" });
        return;
    }
    
    stopSpeaking();
    
    if (!text || text.trim() === '') {
        Logger.warn('AzureTTS', 'Texto vacío, nada que sintetizar');
        callbacks.onEnd?.();
        return;
    }
    
    isSpeaking = true;
    activeSynthesisCallbacks = { ...callbacks };
    
    worker.postMessage({
        type: 'synthesize',
        payload: { text }
    });
}

/**
 * Detiene la síntesis y reproducción de voz
 */
export function stopSpeaking(): void {
    if (worker && isSpeaking) {
        worker.postMessage({ type: 'stop' });
    }
    
    if (currentAudioSource) {
        try {
            currentAudioSource.onended = null;
            currentAudioSource.stop(0);
            currentAudioSource = null;
        } catch (e) {
            const stopError = handleTTSError(e, 'stopSpeaking');
            Logger.error('AzureTTS', 'Error al detener audio', stopError);
        }
    }
    
    audioQueue = [];
    
    if (isSpeaking) {
        isSpeaking = false;
        
        const callbacks = activeSynthesisCallbacks;
        activeSynthesisCallbacks = null;
        
        if (callbacks && callbacks.onEnd) {
            setTimeout(() => {
                callbacks.onEnd?.();
            }, 10);
        }
    }
    
    Logger.tts('Síntesis y reproducción detenidas');
}

/**
 * Comprueba si hay síntesis activa
 */
export function getIsSpeaking(): boolean {
    return isSpeaking;
}

/**
 * Comprueba si el sistema de síntesis está disponible
 */
export function isSynthesisAvailable(): boolean {
    return initialized && !!audioContext;
}
